import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip as MuiTooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

export default function PowerGraph({ data = [], isOffGrid = false, availableKeys = null, currentTimestamp = null, isDark = false, showControls = true, isResilientMode = false, legendPosition = 'right' }) {
  const textPrimary = isDark ? '#ffffff' : vortxColors.charcoal;
  const bgCard = isDark ? '#1e1e1e' : '#ffffff';

  // Pulse animation for cursor
  const pulseStyle = `
    @keyframes pulse {
        0% { stroke-opacity: 0.8; stroke-width: 2px; }
        50% { stroke-opacity: 1; stroke-width: 4px; }
        100% { stroke-opacity: 0.8; stroke-width: 2px; }
    }
    .live-cursor-line {
        animation: pulse 2s infinite;
        filter: drop-shadow(0 0 4px rgba(34, 197, 94, 0.5));
    }
  `;

  // Mode-aware chart colors
  const navyColor = isDark ? '#4A7AB0' : '#1A3A5C';
  const greenColor = isDark ? '#4CAF50' : '#2E7D32';
  const loadLineColor = isDark ? vortxColors.white : vortxColors.charcoal;

  // Positive stack (serving load) bottom->top - Vibrant, distinct colors
  // Order: Grid -> PV -> BESS -> Generator (matching reference image)
  const POS_STACK = [
    { key: 'unmet_load', label: 'Unmet Load', color: '#EF4444', pattern: 'patUnmet' }, // Red for unmet
    { key: 'grid_to_load', label: 'Grid Serves Load', color: navyColor }, // Deep navy / bright blue
    { key: 'grid_charges_bess', label: 'Grid Charges BESS', color: navyColor, pattern: 'patGridCharge', isCharging: true },
    { key: 'pv_to_load', label: 'PV Serves Load', color: '#6BBF59' }, // Bright lime green
    { key: 'pv_charges_bess', label: 'PV Charges BESS', color: '#6BBF59', pattern: 'patPvCharge', isCharging: true },
    { key: 'bess_to_load', label: 'BESS Serves Load', color: '#888888', pattern: 'patBessServe' }, // Medium gray hatched
    { key: 'gen_to_load', label: 'Generator Serves Load', color: greenColor } // Forest green / bright green
  ];

  // Lines configuration
  const LINES = [
    { key: 'load', label: 'Facility Load', color: loadLineColor, dashArray: '8 4' },
    { key: 'net_utility', label: 'Net Utility Load', color: vortxColors.gold, dashArray: '8 4' },
    { key: 'cold_load', label: 'Cold Storage', color: '#7B68EE', dashArray: '4 2' },
    { key: 'ev_load', label: 'EV Charging', color: '#00d4aa', dashArray: '4 2' },
    { key: 'batt_soc', label: 'Battery SOC', color: '#66bb6a', dashArray: '2 2', yAxisId: 'soc' },
  ];

  // Determine which series are present for the UI checkboxes
  const presentKeys = useMemo(() => {
    if (availableKeys) return new Set(availableKeys);

    const keys = new Set();
    if (data.length > 0) {
      Object.keys(data[0]).forEach((k) => {
        if (data.some((row) => row[k] !== undefined && row[k] !== 0)) {
          keys.add(k);
        }
      });
    }
    return keys;
  }, [availableKeys, data]);

  // Initialize visibility state
  const [show, setShow] = useState(() => {
    // If availableKeys passed, use them. Else derive from first data point (legacy behavior)
    if (availableKeys) {
      const keys = new Set(availableKeys);
      return {
        load: keys.has('load'),
        net_utility: keys.has('net_utility') || keys.has('utility'),
        unmet_load: keys.has('utility_residual') || keys.has('unmet_load'),
        grid_to_load: keys.has('utility') || keys.has('utility_residual'),
        grid_charges_bess: keys.has('bess_charge_grid'),
        pv_to_load: keys.has('solar_to_load') || keys.has('solar'),
        pv_charges_bess: keys.has('bess_charge_solar'),
        bess_to_load: keys.has('bess_discharge') || keys.has('bess'),
        gen_to_load: keys.has('genset'),
        cold_load: keys.has('cold_load'),
        ev_load: keys.has('ev_load'),
      };
    }
    const presentKeys = new Set();
    if (data.length > 0) {
      Object.keys(data[0]).forEach((k) => {
        if (data.some((row) => row[k] !== undefined && row[k] !== 0)) {
          presentKeys.add(k);
        }
      });
    }
    return {
      load: presentKeys.has('load'),
      net_utility: presentKeys.has('net_utility') || presentKeys.has('utility'),
      unmet_load: presentKeys.has('utility_residual') || presentKeys.has('unmet_load'),
      grid_to_load: presentKeys.has('utility') || presentKeys.has('utility_residual'),
      grid_charges_bess: presentKeys.has('bess_charge_grid'),
      pv_to_load: presentKeys.has('solar_to_load') || presentKeys.has('solar'),
      pv_charges_bess: presentKeys.has('bess_charge_solar'),
      bess_to_load: presentKeys.has('bess_discharge') || presentKeys.has('bess'),
      gen_to_load: presentKeys.has('genset')
    };
  });

  // Update visibility when availableKeys or data changes
  useEffect(() => {
    let keys;
    if (availableKeys) {
      keys = new Set(availableKeys);
    } else {
      // Fallback to data-driven keys
      keys = new Set();
      if (data.length > 0) {
        Object.keys(data[0]).forEach((k) => {
          if (data.some((row) => row[k] !== undefined && row[k] !== 0)) {
            keys.add(k);
          }
        });
      }
    }

    setShow({
      load: keys.has('load'),
      net_utility: keys.has('net_utility') || keys.has('utility'),
      unmet_load: keys.has('utility_residual') || keys.has('unmet_load'),
      grid_to_load: keys.has('utility'),
      grid_charges_bess: keys.has('bess_charge_grid'),
      pv_to_load: keys.has('solar_to_load') || keys.has('solar'),
      pv_charges_bess: keys.has('bess_charge_solar'),
      bess_to_load: keys.has('bess_discharge') || keys.has('bess'),
      gen_to_load: keys.has('genset'),
      cold_load: keys.has('cold_load'),
      ev_load: keys.has('ev_load'),
    });
  }, [data, availableKeys]);

  const handleToggle = (key) => (e) => {
    setShow({ ...show, [key]: e.target.checked });
  };

  // Export data to CSV
  const handleExport = useCallback(() => {
    if (!data || data.length === 0) return;

    const headers = [
      'Time',
      'Facility Load (kW)',
      'Net Utility (kW)',
      'Grid to Load (kW)',
      'Grid Charges BESS (kW)',
      'PV to Load (kW)',
      'PV Charges BESS (kW)',
      'BESS to Load (kW)',
      'Generator to Load (kW)'
    ];

    const rows = data.map(row => {
      const time = new Date(row.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      return [
        time,
        Math.round(row.load || 0),
        Math.round(row.net_utility || row.utility || 0),
        Math.round(row.utility_residual || row.utility || 0),
        Math.round(Math.abs(row.bess_charge_grid || 0)),
        Math.round(row.solar_to_load || row.solar || 0),
        Math.round(Math.abs(row.bess_charge_solar || 0)),
        Math.round(row.bess_discharge || row.bess || 0),
        Math.round(row.genset || 0)
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'power_flow_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [data]);

  // Use actual data timestamp for start
  const startTime = data[0]?.timestamp || 0;

  // End time calculation
  let endTime;
  if (isResilientMode) {
    // Rolling 24h window
    endTime = startTime + (24 * 3600 * 1000);
  } else {
    // Midnight next day
    const dayStart = new Date(startTime);
    dayStart.setHours(0, 0, 0, 0);
    endTime = dayStart.getTime() + (24 * 3600 * 1000);
  }

  // Build derived data - transform data to new stacking model
  const displayedData = useMemo(() => {
    return data.map(row => {
      // Unmet load
      const unmet_load = show.unmet_load ? (row.utility_residual || row.unmet_load || 0) : 0;
      // Grid serves load (ensure utility_residual doesn't leak here if we have explicit unmet)
      const grid_to_load = show.grid_to_load ? (row.utility || 0) : 0;
      // Grid charging BESS (shown as positive, part of grid stack)
      const grid_charges_bess = show.grid_charges_bess ? Math.abs(row.bess_charge_grid || 0) : 0;
      // PV serves load
      const pv_to_load = show.pv_to_load ? (row.solar_to_load || row.solar || 0) : 0;
      // PV charging BESS (shown as positive, part of PV stack)
      const pv_charges_bess = show.pv_charges_bess ? Math.abs(row.bess_charge_solar || 0) : 0;
      // BESS serves load
      const bess_to_load = show.bess_to_load ? (row.bess_discharge || row.bess || 0) : 0;
      // Generator serves load
      const gen_to_load = show.gen_to_load ? (row.genset || 0) : 0;

      return {
        ...row,
        unmet_load,
        grid_to_load,
        grid_charges_bess,
        pv_to_load,
        pv_charges_bess,
        bess_to_load,
        gen_to_load
      };
    });
  }, [data, show]);

  return (
    <Box sx={{ width: '100%', height: 400 }}>
      {/* Inject styles */}
      <style>{pulseStyle}</style>

      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 600, fontFamily: vortxTypography.heading }}>Power Flow</Typography>
        {showControls && (
          <MuiTooltip title="Export 24hr data as CSV">
            <IconButton
              onClick={handleExport}
              size="small"
              sx={{
                color: textPrimary,
                opacity: 0.7,
                '&:hover': { opacity: 1, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
                padding: '4px'
              }}
            >
              <DownloadIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </MuiTooltip>
        )}
      </Box>
      <Box display="flex" flexDirection={legendPosition === 'bottom' ? 'column' : 'row'} height={legendPosition === 'bottom' ? undefined : 300}>
        <Box flexGrow={1} height={legendPosition === 'bottom' ? 300 : undefined}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={displayedData} margin={{ top: 45, right: 30, bottom: 10, left: 20 }}>
              <defs>
                {/* Grid charging pattern - horizontal stripes on navy */}
                <pattern id="patGridCharge" patternUnits="userSpaceOnUse" width="8" height="8">
                  <rect width="8" height="8" fill={navyColor} />
                  <line x1="0" y1="4" x2="8" y2="4" stroke="#FFFFFF" strokeWidth="2" strokeOpacity="0.5" />
                </pattern>
                {/* PV charging pattern - diagonal stripes on bright green */}
                <pattern id="patPvCharge" patternUnits="userSpaceOnUse" width="8" height="8">
                  <rect width="8" height="8" fill="#6BBF59" />
                  <path d="M0 8 L8 0" stroke="#FFFFFF" strokeWidth="2" strokeOpacity="0.6" />
                </pattern>
                {/* BESS pattern - diagonal hatching on gray */}
                <pattern id="patBessServe" patternUnits="userSpaceOnUse" width="8" height="8">
                  <rect width="8" height="8" fill="#888888" />
                  <path d="M0 8 L8 0 M-2 2 L2 -2 M6 10 L10 6" stroke="#FFFFFF" strokeWidth="2" strokeOpacity="0.6" />
                </pattern>
                {/* Unmet load pattern - subtle diagonal on red */}
                <pattern id="patUnmet" patternUnits="userSpaceOnUse" width="8" height="8">
                  <rect width="8" height="8" fill="#EF4444" />
                  <path d="M0 8 L8 0" stroke="#FFFFFF" strokeWidth="1" strokeOpacity="0.3" />
                </pattern>
              </defs>
              <CartesianGrid stroke={vortxColors.teal} strokeOpacity={isDark ? 0.2 : 0.3} verticalLines={true} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={[startTime, endTime]}
                ticks={(() => {
                  // Generate ticks at even 2-hour intervals covering the full domain
                  const ticks = [];
                  const start = new Date(startTime);

                  // Align first tick to even hour
                  const startHour = start.getHours();
                  const firstTickDate = new Date(start);
                  if (startHour % 2 !== 0) {
                    firstTickDate.setHours(startHour + 1, 0, 0, 0);
                  } else {
                    firstTickDate.setMinutes(0, 0, 0);
                  }

                  let currentTick = firstTickDate.getTime();
                  // Ensure we start within or at domain
                  if (currentTick < startTime) currentTick += 2 * 3600 * 1000;

                  while (currentTick <= endTime) {
                    ticks.push(currentTick);
                    currentTick += 2 * 3600 * 1000; // Add 2 hours
                  }
                  return ticks;
                })()}
                tickFormatter={(t) => {
                  const d = new Date(t);
                  const hours = d.getHours();
                  const timeStr = `${hours.toString().padStart(2, '0')}:00`;

                  // If resilient mode/rolling window, we might want to show day name too if it crosses midnight?
                  // For now, simple time is fine, relying on tooltip for full date.
                  // But let's check if it's "Start of new day"
                  if (hours === 0) {
                    if (isResilientMode) return d.toLocaleDateString([], { weekday: 'short' }); // "Mon"
                    if (t >= endTime - 3600000) return '24:00';
                  }
                  return timeStr;
                }}
                stroke={textPrimary}
                tick={{ fill: textPrimary, fontSize: 11 }}
              />
              <YAxis
                yAxisId="left"
                label={{ value: 'kW', angle: -90, position: 'insideLeft', style: { fill: textPrimary } }}
                stroke={textPrimary}
                tick={{ fill: textPrimary }}
              />
              {show.batt_soc && (
                <YAxis
                  yAxisId="soc"
                  orientation="right"
                  domain={[0, 100]}
                  label={{ value: 'SOC %', angle: 90, position: 'insideRight', style: { fill: '#66bb6a', fontSize: 10 } }}
                  stroke="#66bb6a"
                  tick={{ fill: '#66bb6a', fontSize: 9 }}
                  width={40}
                />
              )}
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload) return null;

                  // Label mappings
                  const labelMap = {
                    'grid_to_load': { label: 'Grid Serves Load', color: navyColor, pattern: false },
                    'grid_charges_bess': { label: 'Grid Charges BESS', color: navyColor, pattern: true },
                    'pv_to_load': { label: 'PV Serves Load', color: '#6BBF59', pattern: false },
                    'pv_charges_bess': { label: 'PV Charges BESS', color: '#6BBF59', pattern: true },
                    'bess_to_load': { label: 'BESS Serves Load', color: '#888888', pattern: true },
                    'gen_to_load': { label: 'Generator Serves Load', color: greenColor, pattern: false },
                    'cold_load': { label: 'Cold Storage', color: '#7B68EE', isDashed: true },
                    'ev_load': { label: 'EV Charging', color: '#00d4aa', isDashed: true },
                    'load': { label: 'Facility Load', color: loadLineColor, isDashed: true },
                    'net_utility': { label: 'Net Utility Load', color: vortxColors.gold, isDashed: true }
                  };

                  return (
                    <div style={{
                      backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderRadius: 4,
                      padding: '8px 12px',
                      fontFamily: vortxTypography.body,
                      fontSize: '0.85rem',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <div style={{ fontWeight: 600, color: textPrimary, marginBottom: 6 }}>
                        {new Date(label).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {payload.map((entry, index) => {
                        const config = labelMap[entry.dataKey] || { label: entry.dataKey.replace(/_/g, ' '), color: entry.color };
                        const swatchStyle = config.isDashed
                          ? { border: `2px dashed ${config.color}`, backgroundColor: 'transparent' }
                          : config.pattern
                            ? { backgroundColor: config.color, backgroundImage: `repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0 2px, transparent 2px 5px)` }
                            : { backgroundColor: config.color };

                        return (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                            <div style={{ width: 14, height: 10, marginRight: 8, borderRadius: 2, ...swatchStyle }} />
                            <span style={{ color: textPrimary, opacity: 0.8 }}>{config.label}:</span>
                            <span style={{ marginLeft: 4, fontWeight: 500, color: textPrimary }}>{Number(entry.value).toFixed(0)} kW</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              {POS_STACK.map(({ key, color, pattern }) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId="pos"
                  yAxisId="left"
                  stroke={color}
                  fill={pattern ? `url(#${pattern})` : color}
                  isAnimationActive={false}
                  hide={!show[key]}
                />
              ))}

              {/* Facility Load line */}
              {show.load && (
                <Line
                  type="monotone"
                  dataKey="load"
                  yAxisId="left"
                  stroke={textPrimary}
                  strokeWidth={3}
                  dot={false}
                  strokeDasharray="8 4"
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {/* Net Utility Load line */}
              {show.net_utility && (
                <Line
                  type="monotone"
                  dataKey="net_utility"
                  yAxisId="left"
                  stroke={vortxColors.gold}
                  strokeWidth={3}
                  dot={false}
                  strokeDasharray="8 4"
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {/* Cold Storage load line (optional — only if data present) */}
              {show.cold_load && (
                <Line
                  type="monotone"
                  dataKey="cold_load"
                  yAxisId="left"
                  stroke="#7B68EE"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="4 2"
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {/* EV Charging load line (optional — only if data present) */}
              {show.ev_load && (
                <Line
                  type="monotone"
                  dataKey="ev_load"
                  yAxisId="left"
                  stroke="#00d4aa"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="4 2"
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {/* Battery SOC line (right Y axis, 0-100%) */}
              {show.batt_soc && (
                <Line
                  type="monotone"
                  dataKey="batt_soc"
                  yAxisId="soc"
                  stroke="#66bb6a"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="2 2"
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {currentTimestamp && (
                <ReferenceLine
                  x={currentTimestamp}
                  stroke="#22c55e"
                  strokeWidth={3}
                  className="live-cursor-line"
                  isFront={true}
                  label={(props) => {
                    const { viewBox } = props;
                    const timeStr = new Date(currentTimestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                    return (
                      <g transform={`translate(${viewBox.x}, ${viewBox.y})`}>
                        <rect x={-26} y={-38} width={52} height={34} fill={isDark ? '#1e1e1e' : 'rgba(255,255,255,0.95)'} rx={4} stroke="#22c55e" strokeWidth={1.5} filter="drop-shadow(0 2px 4px rgba(0,0,0,0.2))" />
                        <text x={0} y={-23} textAnchor="middle" fill="#22c55e" fontSize={11} fontWeight="800" fontFamily={vortxTypography.body} style={{ letterSpacing: '0.05em' }}>NOW</text>
                        <text x={0} y={-9} textAnchor="middle" fill={isDark ? '#eee' : '#111'} fontSize={10} fontWeight="600" fontFamily={vortxTypography.body}>{timeStr}</text>
                      </g>
                    );
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        {legendPosition === 'bottom' ? (
          /* Compact static legend for bottom position (no checkboxes) */
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5, px: 1, justifyContent: 'center' }}>
            {presentKeys.has('load') && (
              <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
                <Box sx={{ width: 14, height: 8, border: `2px solid ${textPrimary}`, backgroundColor: 'transparent' }} />
                <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body, fontSize: '0.75rem' }}>Facility Load</Typography>
              </Box>
            )}
            {!isResilientMode && (
              <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
                <Box sx={{ width: 14, height: 8, border: `2px dashed ${vortxColors.gold}`, backgroundColor: 'transparent' }} />
                <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body, fontSize: '0.75rem' }}>Net Utility Load</Typography>
              </Box>
            )}
            {POS_STACK.map((cfg) => {
              const { key, label, color, pattern } = cfg;
              let hasData = false;
              if (key === 'unmet_load') { hasData = presentKeys.has('utility_residual') || presentKeys.has('unmet_load'); }
              else if (key === 'grid_to_load') { hasData = !isResilientMode && (presentKeys.has('utility') || presentKeys.has('utility_residual')); }
              else if (key === 'grid_charges_bess') { hasData = presentKeys.has('bess_charge_grid'); }
              else if (key === 'pv_to_load') { hasData = presentKeys.has('solar_to_load') || presentKeys.has('solar'); }
              else if (key === 'pv_charges_bess') { hasData = presentKeys.has('bess_charge_solar'); }
              else if (key === 'bess_to_load') { hasData = presentKeys.has('bess_discharge') || presentKeys.has('bess'); }
              else if (key === 'gen_to_load') { hasData = presentKeys.has('genset'); }
              else { hasData = presentKeys.has(key); }
              if (!hasData) return null;
              const swatchStyle = pattern
                ? { backgroundColor: color, backgroundImage: `repeating-linear-gradient(135deg, rgba(0,0,0,0.3) 0 1px, transparent 1px 4px)` }
                : { backgroundColor: color };
              return (
                <Box key={key} display="flex" alignItems="center" sx={{ gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 8, ...swatchStyle }} />
                  <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body, fontSize: '0.75rem' }}>{label}</Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          /* Sidebar legend with checkboxes for right position */
          <Box width={220} ml={2}>
            <FormGroup>
              {presentKeys.has('load') && (
                <FormControlLabel
                  control={<Checkbox checked={show.load} onChange={handleToggle('load')} sx={{ color: vortxColors.gray, '&.Mui-checked': { color: vortxColors.gold } }} />}
                  label={
                    <Box display="flex" alignItems="center">
                      <Box sx={{ width: 16, height: 10, border: `2px solid ${textPrimary}`, backgroundColor: 'transparent', mr: 1 }} />
                      <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body, fontSize: '0.875rem' }}>Facility Load</Typography>
                    </Box>
                  }
                />
              )}
              {!isResilientMode && (
                <FormControlLabel
                  control={<Checkbox checked={show.net_utility} onChange={handleToggle('net_utility')} sx={{ color: vortxColors.gray, '&.Mui-checked': { color: vortxColors.gold } }} />}
                  label={
                    <Box display="flex" alignItems="center">
                      <Box sx={{ width: 16, height: 10, border: `2px dashed ${vortxColors.gold}`, backgroundColor: 'transparent', mr: 1 }} />
                      <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body, fontSize: '0.875rem' }}>Net Utility Load</Typography>
                    </Box>
                  }
                />
              )}
              {POS_STACK.map((cfg) => {
                const { key, label, color, pattern } = cfg;
                let hasData = false;
                if (key === 'unmet_load') {
                  hasData = presentKeys.has('utility_residual') || presentKeys.has('unmet_load');
                } else if (key === 'grid_to_load') {
                  hasData = !isResilientMode && (presentKeys.has('utility') || presentKeys.has('utility_residual'));
                } else if (key === 'grid_charges_bess') {
                  hasData = presentKeys.has('bess_charge_grid');
                } else if (key === 'pv_to_load') {
                  hasData = presentKeys.has('solar_to_load') || presentKeys.has('solar');
                } else if (key === 'pv_charges_bess') {
                  hasData = presentKeys.has('bess_charge_solar');
                } else if (key === 'bess_to_load') {
                  hasData = presentKeys.has('bess_discharge') || presentKeys.has('bess');
                } else if (key === 'gen_to_load') {
                  hasData = presentKeys.has('genset');
                } else {
                  hasData = presentKeys.has(key);
                }
                if (!hasData) return null;
                const swatchStyle = (() => {
                  if (pattern) {
                    return {
                      backgroundColor: color,
                      backgroundImage: `repeating-linear-gradient(135deg, rgba(0,0,0,0.3) 0 1px, transparent 1px 4px)`
                    };
                  }
                  return { backgroundColor: color };
                })();
                return (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox checked={show[key]} onChange={handleToggle(key)} sx={{ color: vortxColors.gray, '&.Mui-checked': { color: vortxColors.gold } }} />}
                    label={
                      <Box display="flex" alignItems="center">
                        <Box sx={{ width: 16, height: 10, mr: 1, ...swatchStyle }} />
                        <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body, fontSize: '0.875rem' }}>{label}</Typography>
                      </Box>
                    }
                  />
                );
              })}
            </FormGroup>
          </Box>
        )}
      </Box>
    </Box>
  );
}
